import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { first, from, map, Observable, tap } from 'rxjs';
import { APP_CONFIG } from '../../../shared/configurations/configurations.factory';
import { Configurations } from '../../../shared/configurations/models/configurations.model';
import { ApiEnumerationModel } from '../DTOs/base-DTOs/api-enumeration-model.dto';
import { ApiObjectModel } from '../DTOs/base-DTOs/api-object-model.dto';
import { ApiStatus } from '../DTOs/base-DTOs/api-status.dto';
import { LeadAssignationDto } from '../DTOs/lead-assignation.dto';
import { LeadAuditDto } from '../DTOs/lead-audit.dto';
import { LeadChangeRequestDto, LeadChangeResponseDto } from '../DTOs/lead-change.dto';
import { LeadCreatedDto, LeadCreateDto } from '../DTOs/lead-create.dto';
import { LeadDto } from '../DTOs/lead.dto';
import { LeadAssignDto, LeadQualificationDto } from '../DTOs/leadOperations.dto';
import { BulkInjectResultDto } from '../DTOs/bulk-injection-result.dto';

type Scope = 'Personal' | 'PersonalHierarchy' | 'Hierarchy' | 'All'
type ViewModel = 'Head' | 'Summary'
type SortCriteria = 'LeadId' | 'ProcessingStatus' | 'QualificationStatus' | 'QualificationOutcome' | 'Created' | 'Updated' | 'LeadId-' | 'ProcessingStatus-' | 'QualificationStatus-' | 'QualificationOutcome-' | 'Created-' | 'Updated-'
type OwnershipKind = 'Assignation' | 'Offer' | 'Qualification'
type ProcessingStatus = 'ToDo' | 'WaitingReview' | 'Qualifiable' | 'PendingOfferConfirmation' | 'PendingAssignment' | 'Assigned' | 'Done'
type QualificationStatus = 'Unknown' | 'Rejected' | 'Qualified' | 'Abandoned' | ''
type QualificationOutcome = 'Unknown' | 'AlreadyCustomer' | 'Interested' | 'NonInterested' | 'Expired' | 'NeverReplied' | 'Spam' | 'Unfitting'
type AuditCategories = 'Assignment' | 'Injection' | 'Log' | 'Offer' | 'Qualification' | 'Refusal'

@Injectable({
  providedIn: 'root'
})
export class LeadProxyService {

  private readonly baseUrl: string

  constructor(@Inject(APP_CONFIG) config: Configurations, private readonly httpClient: HttpClient) {
    this.baseUrl = config.environmentConfig.lobbyBaseUrl + '/lead'
  }

  getLeadById$(id: number | string): Observable<ApiObjectModel<LeadDto>> {
    return this.httpClient.get<ApiObjectModel<LeadDto>>(this.baseUrl + `/${id}`)
  }

  getLeadByExternalId$(externalId: string): Observable<ApiObjectModel<LeadDto>> {
    return this.httpClient.get<ApiObjectModel<LeadDto>>(this.baseUrl + `/external/${externalId}`)
  }

  searchLead$(scope: Scope,

    pageIndex: number | null = null,
    pageSize: number | null = null,

    recentlyModifiedDaysOffset: number | null = null,
    ownerId: number | null = null,
    ownershipKind: OwnershipKind[] | null = null,
    ownerGrantorId: number | null = null,
    omitRequester: boolean | null = null,

    municipalityId: number | null = null,
    provinciaId: number | null = null,
    leadId: number | null = null,
    injectorId: number | null = null,

    processingStatus: ProcessingStatus[] | null = null,
    qualificationStatus: QualificationStatus[] | null = null,
    qualificationOutcome: QualificationOutcome[] | null = null,

    contactPoint: string | null = null,

    sort: SortCriteria | null = null,
    viewModel: ViewModel | null = null,
  ): Observable<ApiEnumerationModel<LeadDto>> {

    let query: string = '';

    if (scope) query = query + `${query ? '&' : ''}scope=${scope}`
    if (pageIndex) query = query + `${query ? '&' : ''}pg_ix=${pageIndex}`
    if (pageSize) query = query + `${query ? '&' : ''}pg_sz=${pageSize}`

    if (recentlyModifiedDaysOffset) query = query + `${query ? '&' : ''}recentlyModifiedDaysOffset=${recentlyModifiedDaysOffset}`
    if (ownerId) query = query + `${query ? '&' : ''}ownerId=${ownerId}`
    if (ownershipKind) ownershipKind.forEach(k => query = query + `${query ? '&' : ''}ownershipKind=${k}`)
    if (ownerGrantorId) query = query + `${query ? '&' : ''}ownerGrantorId=${ownerGrantorId}`
    if (omitRequester) query = query + `${query ? '&' : ''}omitRequester=${omitRequester}`
    if (municipalityId) query = query + `${query ? '&' : ''}comuneId=${municipalityId}`
    if (provinciaId) query = query + `${query ? '&' : ''}provinciaId=${provinciaId}`
    if (contactPoint) query = query + `${query ? '&' : ''}contactPoint=${contactPoint}`
    if (leadId) query = query + `${query ? '&' : ''}leadId=${leadId}`
    if (injectorId) query = query + `${query ? '&' : ''}injectorId=${injectorId}`

    if (processingStatus) processingStatus.forEach(k => query = query + `${query ? '&' : ''}processingStatus=${k}`)
    if (qualificationStatus) qualificationStatus.forEach(s => query = query + `${query ? '&' : ''}qualificationStatus=${s}`)
    if (qualificationOutcome) qualificationOutcome.forEach(o => query = query + `${query ? '&' : ''}qualificationOutcome=${o}`)


    if (sort) query = query + `${query ? '&' : ''}s=${sort}`
    if (viewModel) query = query + `${query ? '&' : ''}vw=${viewModel}`

    return this.httpClient.get<ApiEnumerationModel<LeadDto>>(this.baseUrl + `${query ? '?' : ''}${query}`)
  }

  searchLeadMine$(pageIndex: number | null = null,
    pageSize: number | null = null,
    ownershipKind: OwnershipKind[] | null = null,
    recentlyModifiedDaysOffset: number | null = null,

    ownerGrantorId: number | null = null,
    municipalityId: number | null = null,
    provinciaId: number | null = null,
    leadId: number | null = null,

    processingStatus: ProcessingStatus[] | null = null,
    qualificationStatus: QualificationStatus[] | null = null,
    qualificationOutcome: QualificationOutcome[] | null = null,

    contactPoint: string | null = null,
    sort: SortCriteria | null = null,
    viewModel: ViewModel | null = null,
  ): Observable<ApiEnumerationModel<LeadDto>> {

    let query: string = '';

    if (pageIndex) query = query + `${query ? '&' : ''}pg_ix=${pageIndex}`
    if (pageSize) query = query + `${query ? '&' : ''}pg_sz=${pageSize}`

    if (recentlyModifiedDaysOffset) query = query + `${query ? '&' : ''}RecentlyClosedDayOffset=${recentlyModifiedDaysOffset}`
    if (ownershipKind) ownershipKind.forEach(k => query = query + `${query ? '&' : ''}ownershipKind=${k}`)
    if (ownerGrantorId) query = query + `${query ? '&' : ''}ownerGrantorId=${ownerGrantorId}`
    if (municipalityId) query = query + `${query ? '&' : ''}comuneId=${municipalityId}`
    if (provinciaId) query = query + `${query ? '&' : ''}provinciaId=${provinciaId}`
    if (contactPoint) query = query + `${query ? '&' : ''}contactPoint=${contactPoint}`
    if (leadId) query = query + `${query ? '&' : ''}leadId=${leadId}`

    if (processingStatus) processingStatus.forEach(k => query = query + `${query ? '&' : ''}processingStatus=${k}`)
    if (qualificationStatus) qualificationStatus.forEach(s => query = query + `${query ? '&' : ''}qualificationStatus=${s}`)
    if (qualificationOutcome) qualificationOutcome.forEach(o => query = query + `${query ? '&' : ''}qualificationOutcome=${o}`)

    if (sort) query = query + `${query ? '&' : ''}s=${sort}`
    if (viewModel) query = query + `${query ? '&' : ''}vw=${viewModel}`

    return this.httpClient.get<ApiEnumerationModel<LeadDto>>(this.baseUrl + '/mine' + `${query ? '?' : ''}${query}`)
  }

  postLead$(createDto: LeadCreateDto, dryRun: boolean): Observable<ApiObjectModel<LeadCreatedDto>> {
    return this.httpClient.post<ApiObjectModel<LeadCreatedDto>>(this.baseUrl + (dryRun ? '?dryRun=true' : ''), createDto)
  }

  patchLead$(id: number, version: number, leadChangeRequestDto: Partial<LeadChangeRequestDto>, dryRun: boolean = false): Observable<ApiObjectModel<LeadChangeResponseDto>> {
    return this.httpClient.patch<ApiObjectModel<LeadChangeResponseDto>>(this.baseUrl + `/${id}?ver=${version}` + (dryRun ? '&dryRun=true' : ''), leadChangeRequestDto)
  }

  // Lead Operations

  requestLeadAutoOffer$(leadId: number): Observable<ApiStatus> {
    return this.httpClient.post<ApiStatus>(this.baseUrl + `/${leadId}.RequestAutoOffer`, null)
  }

  revokeLeadOffer$(leadId: number): Observable<ApiStatus> {
    return this.httpClient.post<ApiStatus>(this.baseUrl + `/${leadId}.RevokeOffer`, null)
  }

  revokeLeadAssignation$(leadId: number): Observable<ApiStatus> {
    return this.httpClient.post<ApiStatus>(this.baseUrl + `/${leadId}.RevokeAssignation`, null)
  }

  offerLead$(id: number, assigneeId: number, deadline: Date | null = null): Observable<ApiObjectModel<LeadAssignationDto>> {
    const requestDto: LeadAssignDto =
    {
      assignee: assigneeId,
      deadline: deadline?.toJSON() ?? null
    }

    return this.httpClient.post<ApiObjectModel<LeadAssignationDto>>(this.baseUrl + `/${id}.Offer`, requestDto)
  }

  assignLead$(id: number, assigneeId: number, deadline: Date | null = null): Observable<ApiObjectModel<LeadAssignationDto>> {
    const requestDto: LeadAssignDto =
    {
      assignee: assigneeId,
      deadline: deadline?.toJSON() ?? null
    }

    return this.httpClient.post<ApiObjectModel<LeadAssignationDto>>(this.baseUrl + `/${id}.Assign`, requestDto)
  }

  acceptLeadOffer$(leadId: number | string): Observable<ApiStatus> {
    return this.httpClient.post<ApiStatus>(this.baseUrl + `/${leadId}.AcceptOffer`, null)
  }

  refuseLeadOffer$(leadId: number | string): Observable<ApiStatus> {
    return this.httpClient.post<ApiStatus>(this.baseUrl + `/${leadId}.RefuseOffer`, null)
  }

  qualifyLead$(leadId: number | string, outcome: 'Interested' | 'NonInterested' | 'NeverReplied'): Observable<ApiStatus> {
    const requestDto: LeadQualificationDto =
    {
      outcome: outcome
    }

    return this.httpClient.post<ApiStatus>(this.baseUrl + `/${leadId}.Qualify`, requestDto)
  }



  getLeadAudits$(leadId: number,
    pageIndex: number | undefined = undefined,
    pageSize: number | undefined = undefined,
    categories: AuditCategories[] | undefined = undefined,
    scope: Scope = 'All',
    vw: ViewModel | undefined = undefined,
    sort: 'UserId' | 'OperatorId' | 'Category' | 'Created' | 'Updated' | 'UserId-' | 'OperatorId-' | 'Category-' | 'Created-' | 'Updated-' | undefined = undefined
  ): Observable<ApiEnumerationModel<LeadAuditDto>> {
    let query: string = '';

    if (pageIndex) query = query + `${query ? '&' : ''}pg_ix=${pageIndex}`
    if (pageSize) query = query + `${query ? '&' : ''}pg_sz=${pageSize}`
    if (scope) query = query + `${query ? '&' : ''}scope=${scope}`
    if (categories) categories.forEach(c => query = query + `${query ? '&' : ''}category=${c}`)
    if (vw) query = query + `${query ? '&' : ''}vw=${vw}`
    if (sort) query = query + `${query ? '&' : ''}s=${sort}`

    return this.httpClient.get<ApiEnumerationModel<LeadAuditDto>>(this.baseUrl + `/${leadId}/audit` + `${query ? '?' + query : ''}`)
  }

  rejectLead$(leadId: number | string, outcome: 'Spam' | 'AlreadyCustomer' | 'Unfitting') {
    return this.httpClient.post<ApiStatus>(this.baseUrl + `/${leadId}.Reject`, { outcome: outcome })
  }

  uploadFile$(file: File, injectorKey: number | null = null): Observable<ApiObjectModel<BulkInjectResultDto>> {

    let query: string = '';

    if (injectorKey) query = query + `${query ? '&' : ''}injectorKey=${injectorKey}`

    return this.httpClient.post<ApiObjectModel<BulkInjectResultDto>>(this.baseUrl + `/file` + `${query ? '?' + query : ''}`, file,
      {
        headers: new HttpHeaders().set('Content-Type', file.type)
      }
    )
  }

}
